import React, { useState, useEffect } from 'react';
import CatecheseItemList from '../components/CatecheseItemList';
import EditCatecheseItemModal from '../components/EditCatecheseItemModal';
import { useAuth } from '../context/AuthContext';
import { getItems, addItem, updateItem, deleteItem } from '../services/firestore';
import { exportToExcel } from '../services/export';
import { addGemeenschap, getGemeenschapByLocation } from '../services/gemeenschappen';
import SectionHelpButton from '../components/SectionHelpButton';
import { isEtappeVisible } from '../config/visibilityWindows';
import { trackCatecheseAdded, trackCatecheseEdited, trackCatecheseDeleted, trackExportExecuted } from '../services/analytics';
import './CatechesesPage.css';

/**
 * Catecheses Page
 * Manages the list of catechism items with filtering and CRUD operations
 */
const CatechesesPage = () => {
  const { user, isAdmin } = useAuth();

  // State management
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEtappe, setSelectedEtappe] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);

  const normalizeEmail = (value) => (value || '').trim().toLowerCase();

  const userCanManageItem = (item) => {
    if (!item) return false;
    if (isAdmin) return true;

    const normalizedEmail = normalizeEmail(user?.email);
    const createdBy = normalizeEmail(item.createdBy);
    const toegestaneEmails = Array.isArray(item.toegestaneEmails)
      ? item.toegestaneEmails.map(normalizeEmail).filter(Boolean)
      : [];

    return createdBy === normalizedEmail || toegestaneEmails.includes(normalizedEmail);
  };

  const buildAccessScopedItemData = async (itemData) => {
    const {
      isNieuweGemeenschap,
      ...persistedItemData
    } = itemData;

    let matchedGemeenschap = await getGemeenschapByLocation(persistedItemData.bisdom, persistedItemData.parochie, persistedItemData.gemeenschap);

    if (isNieuweGemeenschap) {
      matchedGemeenschap = await addGemeenschap({
        bisdom: persistedItemData.bisdom,
        parochie: persistedItemData.parochie,
        gemeenschap: persistedItemData.gemeenschap,
        catechistVoornaam: persistedItemData.catechistVoornaam,
        catechistAchternaam: persistedItemData.catechistAchternaam,
        catechistEmail: persistedItemData.catechistEmail,
        verantwoordelijkeVoornaam: '',
        verantwoordelijkeAchternaam: '',
        verantwoordelijkeEmail: ''
      });
    }

    const mappedToegestaneEmails = Array.isArray(matchedGemeenschap?.toegestaneEmails)
      ? matchedGemeenschap.toegestaneEmails
      : [];

    return {
      ...persistedItemData,
      catechistVoornaam: persistedItemData.catechistVoornaam || matchedGemeenschap?.catechistVoornaam || '',
      catechistAchternaam: persistedItemData.catechistAchternaam || matchedGemeenschap?.catechistAchternaam || '',
      catechistEmail: persistedItemData.catechistEmail || matchedGemeenschap?.catechistEmail || '',
      gemeenschapId: matchedGemeenschap?.id || '',
      toegestaneEmails: mappedToegestaneEmails
    };
  };

  // Load items from Firestore on component mount
  useEffect(() => {
    if (user) {
      loadItems();
    }
  }, [user]);

  // Filter items whenever the items array or selected etappe changes
  useEffect(() => {
    // Feature 34: an etappe stays visible for 4 weeks after its date.
    const visibleItems = items.filter(item => isEtappeVisible(item.datum));

    // Then filter by etappe if needed
    if (selectedEtappe === 'all') {
      setFilteredItems(visibleItems);
    } else {
      setFilteredItems(visibleItems.filter(item => item.etappe === selectedEtappe));
    }
  }, [items, selectedEtappe]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const fetchedItems = await getItems();
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitItem = async (itemData) => {
    try {
      const scopedItemData = await buildAccessScopedItemData(itemData);

      // Non-admin users may edit/create only their assigned gemeenschap records
      // or new gemeenschap entries that do not yet exist.
      if (!isAdmin && scopedItemData.gemeenschapId) {
        const normalizedEmail = normalizeEmail(user?.email);
        const hasAccess = scopedItemData.toegestaneEmails
          .map(normalizeEmail)
          .includes(normalizedEmail);

        if (!hasAccess) {
          alert('U kunt alleen etappes beheren voor gemeenschappen waarvoor u verantwoordelijk of catechist bent.');
          return;
        }
      }

      if (editingItem) {
        if (!userCanManageItem(editingItem)) {
          alert('U heeft geen rechten om deze etappe te bewerken.');
          return;
        }

        await updateItem(editingItem.id, scopedItemData);
        setItems(items.map(item =>
          item.id === editingItem.id ? { ...item, ...scopedItemData } : item
        ));
        trackCatecheseEdited({ bisdom: scopedItemData.bisdom, etappe: scopedItemData.etappe });
      } else {
        const addedItem = await addItem(scopedItemData);
        setItems([...items, addedItem]);
        trackCatecheseAdded({ bisdom: scopedItemData.bisdom, etappe: scopedItemData.etappe });
      }
      setIsModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Fout bij het opslaan van het element');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Weet u zeker dat u dit element wilt verwijderen?')) {
      return;
    }

    const itemToDelete = items.find((item) => item.id === id);
    if (!userCanManageItem(itemToDelete)) {
      alert('U heeft geen rechten om deze etappe te verwijderen.');
      return;
    }
    
    try {
      await deleteItem(id);
      setItems(items.filter(item => item.id !== id));
      setIsModalOpen(false);
      setEditingItem(null);
      trackCatecheseDeleted({ bisdom: itemToDelete.bisdom, etappe: itemToDelete.etappe });
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Fout bij het verwijderen van het element');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleAddClick = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleExport = () => {
    exportToExcel(items);
    trackExportExecuted({ export_type: 'catecheses', item_count: items.length });
  };

  const etappes = ['all', ...new Set(items.map(item => item.etappe))];

  return (
    <div className="catecheses-page">
      <div className="app-page-header">
        <div className="app-page-heading">
          <div className="app-page-title-row">
            <h2 className="app-page-title">Catecheses</h2>
            <SectionHelpButton
              title="Catecheses"
              purpose="Deze sectie toont de geplande catecheses en helpt u ze te organiseren per etappe."
              instructions={[
                'Gebruik de etappe-filter om snel de juiste geplande etappes te vinden.',
                'Etappes blijven 4 weken na hun datum zichtbaar; oudere etappes worden verborgen.',
                'Klik op Nieuwe etappe om een item toe te voegen met datum en details.',
                'Gebruik Bewerken op een kaart om informatie aan te passen',
                'Voor admins: gebruik Exporteren voor een overzicht.'
              ]}
            />
          </div>
          <p className="app-page-description">Bekijk, filter en beheer etappes van de afgelopen 4 weken en in de toekomst.</p>
        </div>
      </div>

      <CatecheseItemList
        items={filteredItems}
        etappes={etappes}
        selectedEtappe={selectedEtappe}
        onEtappeChange={setSelectedEtappe}
        onAddClick={handleAddClick}
        onEditItem={handleEditItem}
        onExport={handleExport}
        loading={loading}
        currentUserEmail={user?.email}
        isAdmin={isAdmin}
      />
      <EditCatecheseItemModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitItem}
        isEditMode={editingItem !== null}
        initialData={editingItem}
        onDelete={handleDeleteItem}
      />
    </div>
  );
};

export default CatechesesPage;
